import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Users, Trophy } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const SURVEY_LABELS = {
  control: 'Sense of Control',
  productivity: 'Productivity',
  efficiency: 'Efficiency',
  contentment: 'Contentment',
  self_esteem: 'Self-esteem',
  concentration: 'Concentration',
  work_life_balance: 'Work-Life Balance'
};

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px'
};

export const CommunityComparisonCard = ({ communityData, optedIn: initialOptedIn, onOptInChange }) => {
  const [optedIn, setOptedIn] = useState(initialOptedIn ?? false);

  const handleToggle = async (value) => {
    try {
      await api.put('/user/consent', { compare_stats_opted_in: value });
      setOptedIn(value);
      if (onOptInChange) onOptInChange(value);
      toast.success(value ? 'Opted in to community comparison' : 'Opted out of community comparison');
    } catch {
      toast.error('Failed to update preference');
    }
  };

  // Not opted in — show toggle + invitation
  if (!optedIn || !communityData || !communityData.has_community_data) {
    return (
      <Card className="glass border-white/20 dark:border-white/10">
        <CardHeader>
          <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
            <Users className="w-5 h-5" /> Community Comparison
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Compare my stats to other users</Label>
              <p className="text-sm text-muted-foreground">Anonymously compare your achievements and wellbeing with other Brioritizer users</p>
            </div>
            <Switch checked={optedIn} onCheckedChange={handleToggle} data-testid="community-opt-in-toggle" />
          </div>
          {optedIn && communityData && !communityData.has_community_data && (
            <p className="text-sm text-muted-foreground text-center pt-2">{communityData.message}</p>
          )}
          {!optedIn && (
            <p className="text-sm text-muted-foreground text-center pt-2">Opt in to see how you compare against the community</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const moodChartData = Object.entries(SURVEY_LABELS).map(([key, label]) => ({
    metric: label.replace('Work-Life Balance', 'Work-Life').replace('Sense of Control', 'Control'),
    You: communityData.your_stats.latest_survey?.[key] ?? 0,
    Community: communityData.last_10_wellbeing_aggregate?.[key] ?? 0
  }));

  return (
    <Card className="glass border-white/20 dark:border-white/10">
      <CardHeader>
        <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
          <Users className="w-5 h-5" /> Community Comparison
        </CardTitle>
        <CardDescription>
          Your stats compared to {communityData.opted_in_count} opted-in Brioritizer users
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Compare my stats to other users</Label>
            <p className="text-sm text-muted-foreground">Anonymously compare your achievements and wellbeing</p>
          </div>
          <Switch checked={optedIn} onCheckedChange={handleToggle} data-testid="community-opt-in-toggle" />
        </div>

        {/* Your stats vs Community average */}
        <div>
          <Label className="text-sm font-medium mb-3 block">You vs Community Average</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-center">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{communityData.your_stats.achievements}</p>
              <p className="text-xs text-muted-foreground mt-1">Your Achievements</p>
              <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">avg: {communityData.community_average.achievements}</p>
            </div>
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 text-center">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{communityData.your_stats.interests}</p>
              <p className="text-xs text-muted-foreground mt-1">Your Interests</p>
              <p className="text-xs text-blue-600/70 dark:text-blue-400/70">avg: {communityData.community_average.interests}</p>
            </div>
          </div>
        </div>

        {/* Average Achievements by Category */}
        {communityData.community_average.beneficiary_breakdown && (
          <CommunityBeneficiaryChart breakdown={communityData.community_average.beneficiary_breakdown} />
        )}

        {/* Wellbeing: You vs Community */}
        {communityData.last_10_wellbeing_aggregate && communityData.your_stats.latest_survey && (
          <div>
            <Label className="text-sm font-medium mb-3 block">Your Mood vs Community (Last 10 Surveys)</Label>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={moodChartData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="metric" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="Community" fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={16} />
                <Bar dataKey="You" fill="#10b981" radius={[4, 4, 0, 0]} barSize={16} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Performer */}
        {communityData.top_performer && (
          <TopPerformerSection topPerformer={communityData.top_performer} yourStats={communityData.your_stats} />
        )}
      </CardContent>
    </Card>
  );
};

const TopPerformerSection = ({ topPerformer, yourStats }) => {
  if (topPerformer.is_you) {
    return (
      <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg" data-testid="top-performer-badge">
              <Trophy className="w-8 h-8 text-white" />
            </div>
          </div>
          <div>
            <p className="font-bold text-amber-700 dark:text-amber-300 text-lg">The World&apos;s Best Brioritizer</p>
            <p className="text-sm text-muted-foreground mt-1">
              You lead the community with {topPerformer.achievements} achievements!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          <Label className="font-medium">Top Brioritizer&apos;s scores are</Label>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Their achievements: <span className="font-semibold text-amber-600 dark:text-amber-400">{topPerformer.achievements}</span></span>
          <span className="text-muted-foreground">Yours: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{yourStats.achievements}</span></span>
        </div>
        {topPerformer.latest_survey && yourStats.latest_survey && (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground mb-2">Their mood vs yours</p>
            <div className="grid grid-cols-7 gap-1 text-center">
              {Object.entries(SURVEY_LABELS).map(([key, label]) => (
                <div key={key} className="text-xs">
                  <p className="text-muted-foreground truncate" title={label}>{label.split(' ')[0]}</p>
                  <p className="font-semibold text-amber-600 dark:text-amber-400">{topPerformer.latest_survey?.[key] ?? 0}</p>
                  <p className="font-semibold text-emerald-600 dark:text-emerald-400">{yourStats.latest_survey?.[key] ?? 0}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-4 mt-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Top</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> You</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const BENEFICIARY_DATA_CONFIG = [
  { key: 'me', name: 'Personal', color: '#10B981' },
  { key: 'work', name: 'Work', color: '#3B82F6' },
  { key: 'others', name: 'Others', color: '#8B5CF6' },
  { key: 'no_one', name: 'No one', color: '#6B7280' }
];

const CommunityBeneficiaryChart = ({ breakdown }) => {
  const data = BENEFICIARY_DATA_CONFIG
    .map(({ key, name, color }) => ({ name, value: breakdown[key] || 0, color }))
    .filter(d => d.value > 0);

  if (data.length === 0) return null;

  return (
    <div>
      <Label className="text-sm font-medium mb-3 block">Avg. Achievements by Category</Label>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value">
            {data.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => `${value} avg`} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-4 justify-center mt-2">
        {data.map((entry) => (
          <div key={entry.name} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-sm">{entry.name}: {entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
